//-- Copyright (c) 2021 TMT Insights Group, LLC
import React, { CSSProperties, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import axios from '@/_utils/axios';
import PolarisIcon from '@/components/_common/PolarisIcon';
import { Exception, OrderDetail, UserGroup, UserProfile } from '@/redux/slices/slice_types';
import { Button, ClickAwayListener, hexToRgb, Popper, SxProps, Theme } from '@mui/material';
import { assignGroupToTask, assignUserToTask, editOrder, getData } from '@/redux/slices/dataTableSlice';
import { getNotifications } from '@/redux/slices/notificationSlice';
import { refreshCurrentTask } from '@/redux/slices/taskDrawerSlice';
import { selectCanWritePendingAssets, selectCanWriteTasks } from '@/redux/selectors/authSelectors';
import { useToast } from '../Toast';
import GroupDisplay from './GroupDisplay';
import GroupSearch from './GroupSearch';
import UserDisplay from './UserDisplay';
import UserSearch from './UserSearch';
import { useConfigValue } from '@/components/_hooks/useConfigValue';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useTheme } from '@mui/material/styles';
import { AutocompleteProps } from '../Form/Autocomplete';
import { selectNotificationGroupTypes } from '@/redux/selectors/notificationSelectors';

// this component is also used as a widget in schema forms
// when used within schema forms, the value is a string (as opposed to a user object as it is throughout polaris)
// in this component and child components, we need to handle both cases so there are checks for `user` being a string
type AllAssigningOptions = 'user' | 'group';

type TaskType = {
  update?: 'task';
  isAsset?: boolean;
} & (
  | {
      value: UserGroup;
      assigns: Extract<AllAssigningOptions, 'group'>;
    }
  | {
      value: UserProfile;
      assigns: Extract<AllAssigningOptions, 'user'>;
      priority?: null | number;
    }
  | {
      value: string;
      assigns: Extract<AllAssigningOptions, 'user'>;
      priority: null | number;
      onChange: (val: string) => void;
    }
);

type OrderType = {
  update: 'order';
  value: UserProfile;
  assigns: 'user';
  isAsset?: false;
  row: OrderDetail;
};

type ExceptionType = {
  update: 'exception';
  value: UserProfile | null;
  assigns: 'user';
  isAsset?: false;
  row: Exception;
  onChange?: (user: UserProfile) => void;
};

type UpdateFunction = AutocompleteProps['onChange'];
type UpdateFunctions = {
  task: { group: UpdateFunction; user: UpdateFunction };
  order: { user: UpdateFunction };
  exception: { user: UpdateFunction };
};

type AssignsConfig = Record<
  AssigneeSelectProps['assigns'],
  {
    search: () => ReactNode;
    display: () => ReactNode;
  }
>;

export type AssigneeSelectProps = {
  id?: string | number | undefined;
  viewOnly?: boolean;
  emptyText?: string;
  popper?: boolean;
  hideUnassigned?: boolean;
  userDisplayIconStyle?: CSSProperties;
  assignButtonSx?: SxProps<Theme>;
  unnassignedAsInt?: boolean;
} & (TaskType | OrderType | ExceptionType);

const evaluateCanAssign = (id: AssigneeSelectProps['id'], value: string): boolean => {
  return !!(id || value === 'string');
};

const resetPriority = (id: string) => {
  return axios.post(`/core/tasks/${id}/priority/`, { priority: null });
};

const AssigneeSelect = (props: AssigneeSelectProps) => {
  const {
    update = 'task',
    isAsset = false,
    id,
    viewOnly,
    emptyText,
    value,
    assigns = 'user',
    popper,
    hideUnassigned,
    userDisplayIconStyle,
    assignButtonSx,
    unnassignedAsInt,
    ...extra
  } = props;

  const theme = useTheme();
  const dispatch = useAppDispatch();
  const [selected, setSelected] = useState<any>(value);
  const canWriteTasks = useAppSelector(selectCanWriteTasks);
  const canWritePendingAssets = useAppSelector(selectCanWritePendingAssets);
  const groupTypes = useAppSelector(selectNotificationGroupTypes);
  const notificationsEnabled = useConfigValue(['notifications']);
  const [view, setView] = useState<'display' | 'loading' | 'assign'>('display');
  const canAssign = evaluateCanAssign(id, typeof value);
  const hoverLabel = assigns.length > 0 ? assigns.charAt(0).toUpperCase() + assigns.slice(1) : '';
  const userRef = useRef<any>(null);
  const { success, error, warn } = useToast();

  let isViewOnly = viewOnly || ((extra as OrderType).row && (extra as OrderType).row?.status === 'draft');
  if (update === 'task' && !canWriteTasks) isViewOnly = true;
  else if (isAsset && !canWritePendingAssets) isViewOnly = true;

  useEffect(() => {
    if (view === 'assign') {
      setTimeout(() => {
        userRef.current.querySelector('.MuiIconButton-root')?.click();
        userRef.current.querySelector('input')?.select();
      }, 100);
    }
  }, [view]);

  const updateFunctions: UpdateFunctions = {
    task: {
      group: (_: any, newGroup: UserGroup) => {
        setView('loading');
        if (typeof value === 'string' || !id) {
          // TASK => Schema Form GROUP ASSIGNMENT
          // NOT CURRENTLY ENABLED
          console.error('Schema form not enabled for group assignment');
          setView('display');
        } else if (selected === null || (selected?.id && selected.id !== newGroup.id)) {
          // TASK => Default GROUP ASSIGNMENT
          (dispatch(assignGroupToTask({ id: Number(id), group: newGroup, type: isAsset ? 'assets' : 'tasks' })) as any)
            .unwrap()
            .then((res: any) => {
              success('Group has been assigned!');
              setSelected(res.assigned_user);
              dispatch(getData());
            })
            .catch((err: any) => {
              error(err.data?.message || 'There was a problem assigning the task.');
            })
            .finally(() => setView('display'));
        } else {
          setView('display');
        }
      },
      user: (_: any, newUser: UserProfile) => {
        setView('loading');

        const makeUserAssignment = () => {
          (dispatch(assignUserToTask({ id: Number(id), user: newUser, type: isAsset ? 'assets' : 'tasks' })) as any)
            .unwrap()
            .then((res: any) => {
              success('Task has been assigned!');
              setSelected(res.assigned_user);
            })
            .finally(() => {
              setView('display');
              dispatch(getData());
              dispatch(refreshCurrentTask(id as string));
              const taskGroup = groupTypes.find((g) => g.toLowerCase() === 'tasks');
              if (notificationsEnabled && taskGroup) {
                dispatch(getNotifications({ page: 0, group_type: taskGroup, seen: false }));
              }
            });
        };

        if (hideUnassigned || unnassignedAsInt) {
          (extra as { onChange: (val: UserProfile) => void }).onChange(newUser);
          setView('display');
        } else if (typeof value === 'string' || !id) {
          // TASK => Schema Form USER ASSIGNMENT
          (extra as { onChange: (val: string) => void }).onChange(!newUser.id ? '' : `${newUser?.first_name} ${newUser?.last_name}`);
          setView('display');
        } else if (selected === null && newUser.id === -1) {
          // no change - don't assign task to unassigned when its already unassigned
          setView('display');
        } else if (selected === null || (selected?.id && selected.id !== newUser.id)) {
          // TASK => Default USER ASSIGNMENT
          if ((extra as { priority: null | number }).priority) {
            // if the task has priority, reset the priority on assign
            resetPriority(id as string)
              .then((res) => {
                if (!res.data?.success) throw new Error();
                warn('Priority ranking has been cleared.');
                makeUserAssignment();
              })
              .catch((err) => {
                console.error('Problem clearing priority ranking: ', err);
                error(err.response || 'There was an error clearing priority ranking.');
              })
              .finally(() => {
                setView('display');
              });
          } else {
            // if no priority, just assign the new user
            makeUserAssignment();
          }
        } else {
          setView('display');
        }
      }
    },
    order: {
      user: (_: any, newUser: UserProfile) => {
        setView('loading');
        (
          dispatch(
            editOrder({
              description: (extra as OrderType).row.description,
              po_number: (extra as OrderType).row.po_number,
              id: id as number,
              managed_by: String(newUser && newUser.id < 0 ? 0 : newUser.id)
            })
          ) as any
        )
          .unwrap()
          .then(() => {
            getData();
            success('Order has been updated');
          })
          .catch((err: any) => {
            error(err.data?.message || 'There was a problem updating the order.');
          })
          .finally(() => {
            setView('display');
          });
      }
    },
    exception: {
      user: (_: unknown, newUser: UserProfile) => {
        setView('loading');
        axios
          .post(`/monitoring/events/${id}/assign/`, newUser.id !== -1 ? { user_id: newUser.id } : {})
          .then(() => {
            success('Exception has been updated');
            dispatch(getData());

            // Just a guard to narrow props type.
            if (props.update !== 'exception') {
              throw new Error('update prop value should be equal to "exception"');
            }

            props.onChange?.(newUser);
          })
          .catch((err: any) => {
            error(err.data?.message || 'There was a problem updating the Exception.');
          })
          .finally(() => {
            setView('display');
          });
      }
    }
  };

  useEffect(() => {
    setSelected(value);
  }, [value]);

  const selectedUnassignedOrNull = useMemo(() => {
    if (unnassignedAsInt && !selected) return false;
    if (unnassignedAsInt && ((typeof selected === 'string' && selected.indexOf('Unassigned') !== -1) || selected.id === -1)) return true;
    if (unnassignedAsInt) return false;
    if (!selected || selected?.id === -1) {
      return true;
    }

    return false;
  }, [update, selected]);

  const sharedSearchProps = {
    value: selected,
    innerRef: userRef,
    onBlur: () => setView('display')
  };

  const assignsConfig: AssignsConfig = {
    group: {
      search: () => {
        if (updateFunctions[update] && update === 'task') {
          return <GroupSearch onSelect={updateFunctions[update].group} {...sharedSearchProps} />;
        } else {
          return <span style={{ opacity: 0.5, fontStyle: 'italic' }}>Not enabled</span>;
        }
      },
      display: () => <GroupDisplay group={value as UserGroup} canAssign={canAssign} emptyText={emptyText} />
    },
    user: {
      search: () => {
        const anchor = document.querySelector(`#assignee-select-${id}`);
        const search = (
          <UserSearch
            isActive
            hideUnassigned={selectedUnassignedOrNull || hideUnassigned}
            onEscape={() => setView('display')}
            onSelect={updateFunctions[update].user}
            {...sharedSearchProps}
          />
        );

        return anchor && popper ? (
          <Popper sx={{ background: theme.palette.background.default }} open anchorEl={anchor} placement="bottom-end">
            {search}
          </Popper>
        ) : (
          search
        );
      },
      display: () =>
        typeof value === 'string' ? (
          value
        ) : (
          <UserDisplay
            key={selected?.id}
            user={selected as UserProfile}
            canAssign={canAssign}
            emptyText={emptyText}
            ellipsisOverflow
            iconStyle={userDisplayIconStyle}
          />
        )
    }
  };

  const renderView = () => {
    const displayComponentProps = {
      className: 'display-wrapper',
      hoverable: +!isViewOnly,
      onClick: !isViewOnly
        ? (e: React.MouseEvent) => {
            e.stopPropagation();
            setView('assign');
          }
        : undefined
    };

    const assignButton = (
      <TextButton id="assign-button" data-testid="assign-button" size="small" color="primary" variant="text" sx={assignButtonSx}>
        Assign {hoverLabel}
      </TextButton>
    );

    switch (view) {
      case 'display':
        if ((hideUnassigned || unnassignedAsInt) && value === null) {
          return <DisplayShowButton {...displayComponentProps}>{assignButton}</DisplayShowButton>;
        }
        return (
          <Display {...displayComponentProps}>
            {assignButton}
            <div id="display">{assignsConfig[assigns].display()}</div>
          </Display>
        );
      case 'assign':
        return (
          <ClickAwayListener onClickAway={() => setView('display')}>
            <div>{assignsConfig[assigns].search()}</div>
          </ClickAwayListener>
        );
      case 'loading':
        return (
          <SpinnerSpan>
            <LoadingIcon icon="loading_dots" />
          </SpinnerSpan>
        );
    }
  };

  return (
    <Wrapper className="assignee-select" id={`assignee-select-${id}`} data-testid="assignee-select" canAssign={+canAssign} hoverable={+!isViewOnly}>
      {renderView()}
    </Wrapper>
  );
};

export default AssigneeSelect;

const SpinnerSpan = styled.span`
  display: inline-flex;
  color: ${({ theme }) => theme.palette.primary.main};
  margin-left: ${({ theme }) => theme.spacing(4)};
`;

const LoadingIcon = styled(PolarisIcon)`
  height: 2rem;
  width: 2rem;
`;

const TextButton = styled(Button)`
  white-space: nowrap;
  position: absolute;
  user-select: none;
  z-index: 1;
  background-color: ${({ theme }) => {
    const rgb = hexToRgb(theme.palette.primary.main);
    return `rgba(${rgb.slice(4, -1)}, .1)`;
  }};
`;

const Display = styled.div<{ hoverable: number }>`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  position: relative;
  max-width: 100%; // required for "text-overflow: ellipsis;" to work properly inside UserDisplay component

  #display {
    max-width: 100%; // required for "text-overflow: ellipsis;" to work properly inside UserDisplay component
    opacity: 1;
    transition: opacity 0.2s ease-in-out;
  }

  #assign-button {
    display: none;
  }

  ${({ hoverable }) =>
    hoverable &&
    `
  #assign-button {
    opacity: 0;
    display: flex;
    transition: opacity 0.2s ease-in-out;
  }

  &:hover {
    #display {
      opacity: 0;
    }

    #assign-button {
      opacity: 1;
    }
  }
  
  `}
`;

const DisplayShowButton = styled(Display)`
  margin-top: ${(props) => props.theme.spacing(1.5)};
  #assign-button {
    display: flex;
    opacity: 1;
  }
`;

const Wrapper = styled.div<{ canAssign: number; hoverable: number }>`
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  line-height: 1.2;
  position: relative;
  padding-top: 3px;

  white-space: ${({ canAssign }) => (canAssign ? 'nowrap' : 'normal')};

  &.MuiInputBase-inputSizeSmall {
    font-size: 0.9rem;
  }

  > {
    cursor: ${({ canAssign, hoverable }) => (canAssign && hoverable ? 'pointer' : 'auto')};
  }

  ${({ canAssign, hoverable }) =>
    canAssign &&
    hoverable &&
    `
    width: 200px;
  `}
`;
